import { useEffect, useRef, useState } from 'react';
import { useUploadQueue } from '@/upload/UploadQueueContext';
import { getAllAlbums, type AlbumData } from '@/services/albumService';

interface SelectedFile {
  file: File;
  previewUrl: string;
}

type OrganizeMode = 'date' | 'album';

const MAX_FILES = 20;
const MAX_PHOTO_BYTES = 50 * 1024 * 1024;
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
const MAX_VIDEOS = 5;
const VIDEO_TYPES = new Set(['video/mp4', 'video/quicktime', 'video/x-quicktime', 'video/mov', 'application/quicktime']);
const IMAGE_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
  'image/heic-sequence', 'image/heif-sequence', 'image/x-heic', 'image/x-heif',
  'application/heic', 'application/heif',
]);
const VIDEO_EXTENSIONS = new Set(['.mp4', '.mov']);
const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.hif']);
const HEIF_BRANDS = new Set(['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'mif1', 'msf1', 'MiHE', 'MiHB', 'MiPr', 'miaf', 'MA1A', 'MA1B', 'avif', 'avis']);
const VIDEO_BRANDS = new Set(['qt  ', 'mp41', 'mp42', 'isom', 'iso2', 'avc1', 'M4V ', 'MSNV']);

type DetectedFile = {
  kind: 'photo' | 'video' | 'unknown';
  mime: string;
  extension: string;
};

function fileExtension(file: File): string {
  const dot = file.name.lastIndexOf('.');
  return dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
}

function ascii(bytes: Uint8Array, start: number, length: number): string {
  return Array.from(bytes.slice(start, start + length))
    .map((value) => String.fromCharCode(value))
    .join('');
}

function withExtension(name: string, extension: string): string {
  const safeName = name?.trim() || `upload_${Date.now()}`;
  const dot = safeName.lastIndexOf('.');
  const base = dot > 0 ? safeName.slice(0, dot) : safeName;
  return `${base}${extension}`;
}

async function detectFile(file: File): Promise<DetectedFile> {
  const bytes = new Uint8Array(await file.slice(0, 256).arrayBuffer());
  const extension = fileExtension(file);
  const mime = (file.type || '').toLowerCase();

  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return { kind: 'photo', mime: 'image/jpeg', extension: '.jpg' };
  }
  if (bytes.length >= 8 && bytes[0] === 0x89 && ascii(bytes, 1, 3) === 'PNG') {
    return { kind: 'photo', mime: 'image/png', extension: '.png' };
  }
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') {
    return { kind: 'photo', mime: 'image/webp', extension: '.webp' };
  }
  if (bytes.length >= 12 && ascii(bytes, 4, 4) === 'ftyp') {
    const declaredSize = (bytes[0] * 0x1000000) + (bytes[1] << 16) + (bytes[2] << 8) + bytes[3];
    const boxEnd = declaredSize >= 16 && declaredSize <= bytes.length ? declaredSize : bytes.length;
    const brands = new Set<string>([ascii(bytes, 8, 4)]);
    for (let offset = 16; offset + 3 < boxEnd; offset += 4) brands.add(ascii(bytes, offset, 4));
    const heif = Array.from(brands).some((brand) => HEIF_BRANDS.has(brand));
    const video = Array.from(brands).some((brand) => VIDEO_BRANDS.has(brand));
    if (heif) {
      return { kind: 'photo', mime: 'image/heic', extension: extension === '.heif' || extension === '.hif' ? extension : '.heic' };
    }
    if (video) {
      const isMov = brands.has('qt  ') || extension === '.mov';
      return { kind: 'video', mime: isMov ? 'video/quicktime' : 'video/mp4', extension: isMov ? '.mov' : '.mp4' };
    }
    if (IMAGE_TYPES.has(mime) || IMAGE_EXTENSIONS.has(extension)) {
      return { kind: 'photo', mime: 'image/heic', extension: extension || '.heic' };
    }
    if (VIDEO_TYPES.has(mime) || VIDEO_EXTENSIONS.has(extension)) {
      return { kind: 'video', mime: extension === '.mov' ? 'video/quicktime' : 'video/mp4', extension: extension || '.mp4' };
    }
  }

  if (IMAGE_TYPES.has(mime) || IMAGE_EXTENSIONS.has(extension)) {
    return { kind: 'photo', mime: IMAGE_TYPES.has(mime) ? mime : 'application/octet-stream', extension };
  }
  if (VIDEO_TYPES.has(mime) || VIDEO_EXTENSIONS.has(extension)) {
    return { kind: 'video', mime: VIDEO_TYPES.has(mime) ? mime : 'application/octet-stream', extension };
  }
  return { kind: 'unknown', mime, extension };
}

async function normalizeSafariFile(file: File): Promise<File | null> {
  const detected = await detectFile(file);
  if (detected.kind === 'unknown') return null;

  const targetName = detected.extension ? withExtension(file.name, detected.extension) : file.name;
  const targetType = detected.mime || file.type || 'application/octet-stream';
  if (targetName === file.name && targetType === file.type) return file;

  return new File([file], targetName, {
    type: targetType,
    lastModified: file.lastModified,
  });
}

function isVideoFile(file: File): boolean {
  return VIDEO_TYPES.has(file.type) || VIDEO_EXTENSIONS.has(fileExtension(file));
}

function isHeicFile(file: File): boolean {
  return ['.heic', '.heif', '.hif'].includes(fileExtension(file))
    || ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence',
      'image/x-heic', 'image/x-heif', 'application/heic', 'application/heif'].includes(file.type);
}

function isMovFile(file: File): boolean {
  return fileExtension(file) === '.mov'
    || ['video/quicktime', 'video/x-quicktime', 'video/mov', 'application/quicktime'].includes(file.type);
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const megabytes = bytes / (1024 * 1024);
  if (megabytes >= 100) return `${megabytes.toFixed(0)} MB`;
  if (megabytes >= 10) return `${megabytes.toFixed(1)} MB`;
  return `${megabytes.toFixed(2)} MB`;
}

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export default function UploadPage() {
  const { enqueueUpload } = useUploadQueue();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [albums, setAlbums] = useState<AlbumData[]>([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState('');
  const [organizeMode, setOrganizeMode] = useState<OrganizeMode>('date');
  const [isDragging, setIsDragging] = useState(false);
  const [addingToQueue, setAddingToQueue] = useState(false);
  const [queueMessage, setQueueMessage] = useState('');
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getAllAlbums().then(setAlbums).catch(() => setError('앨범 목록을 불러오지 못했습니다.'));
  }, []);

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (selectedFiles.length > 0 && !uploaded) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [selectedFiles.length, uploaded]);

  const handleFileSelect = async (files: FileList | File[] | null) => {
    if (!files || addingToQueue) return;
    const sourceFiles = Array.from(files);
    if (selectedFiles.length + sourceFiles.length > MAX_FILES) {
      setError(`한 번에 최대 ${MAX_FILES}개까지 선택할 수 있습니다.`);
      return;
    }

    const accepted: SelectedFile[] = [];
    let rejected = 0;
    const existingVideos = selectedFiles.filter(({ file }) => isVideoFile(file)).length;
    let addedVideos = 0;

    for (const sourceFile of sourceFiles) {
      let file: File | null = null;
      try {
        file = await normalizeSafariFile(sourceFile);
      } catch {
        file = null;
      }
      if (!file) {
        rejected += 1;
        continue;
      }

      const isVideo = isVideoFile(file);
      const maxBytes = isVideo ? MAX_VIDEO_BYTES : MAX_PHOTO_BYTES;
      if (file.size > maxBytes || (isVideo && existingVideos + addedVideos >= MAX_VIDEOS)) {
        rejected += 1;
        continue;
      }

      if (isVideo) addedVideos += 1;
      const skipPreview = isHeicFile(file) || isMovFile(file);
      accepted.push({ file, previewUrl: skipPreview ? '' : URL.createObjectURL(file) });
    }

    setSelectedFiles((current) => [...current, ...accepted]);
    setError(rejected ? `Safari에서 전달된 형식까지 검사한 뒤 사용할 수 없는 파일 ${rejected}개를 제외했습니다.` : '');
  };

  const removeFile = (index: number) => {
    setSelectedFiles((current) => {
      if (current[index].previewUrl) URL.revokeObjectURL(current[index].previewUrl);
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const clearAll = () => {
    selectedFiles.forEach(({ previewUrl }) => { if (previewUrl) URL.revokeObjectURL(previewUrl); });
    setSelectedFiles([]);
    setError('');
  };

  const handleUpload = async () => {
    if (addingToQueue || selectedFiles.length === 0) return;
    if (organizeMode === 'album' && !selectedAlbumId) {
      setError('사진을 저장할 앨범을 선택해 주세요.');
      return;
    }

    setAddingToQueue(true);
    setError('');
    setQueueMessage('');
    try {
      const queuedFiles = selectedFiles.map(({ file }) => ({
        file,
        kind: isVideoFile(file) ? 'video' as const : 'photo' as const,
      }));
      enqueueUpload(queuedFiles, {
        mode: organizeMode,
        albumId: organizeMode === 'album' ? selectedAlbumId : undefined,
      });
      const count = selectedFiles.length;
      selectedFiles.forEach(({ previewUrl }) => { if (previewUrl) URL.revokeObjectURL(previewUrl); });
      setSelectedFiles([]);
      setUploaded(true);
      setQueueMessage(`${count}개 파일을 작업 대기열에 추가했습니다. 다른 메뉴를 사용해도 업로드는 계속됩니다.`);
    } catch (queueError) {
      setError(queueError instanceof Error ? queueError.message : '작업 대기열에 추가하지 못했습니다.');
    } finally {
      setAddingToQueue(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="px-4 pb-6 pt-12">
        <span className="text-xs font-semibold uppercase tracking-wide text-accent-500">사진 올리기</span>
        <h1 className="mt-1 text-2xl font-bold text-foreground-950">추억을 날짜별로 정리해 보세요</h1>
        <p className="mt-2 text-sm text-foreground-500">
          사진의 촬영일을 확인해 같은 날의 사진끼리 자동으로 앨범을 만듭니다.
        </p>
      </header>

      <main className="flex flex-1 flex-col px-4">
        <section className="mb-5 grid grid-cols-2 gap-2 rounded-2xl bg-secondary-50 p-1.5">
          <button
            type="button"
            onClick={() => setOrganizeMode('date')}
            disabled={addingToQueue}
            className={`rounded-xl px-3 py-3 text-sm font-semibold transition ${
              organizeMode === 'date'
                ? 'bg-background-50 text-accent-600 shadow-sm'
                : 'text-foreground-500'
            }`}
          >
            날짜별 자동 분류
          </button>
          <button
            type="button"
            onClick={() => setOrganizeMode('album')}
            disabled={addingToQueue}
            className={`rounded-xl px-3 py-3 text-sm font-semibold transition ${
              organizeMode === 'album'
                ? 'bg-background-50 text-accent-600 shadow-sm'
                : 'text-foreground-500'
            }`}
          >
            기존 앨범 선택
          </button>
        </section>

        {organizeMode === 'date' ? (
          <div className="mb-4 rounded-xl border border-accent-100 bg-accent-50/60 px-4 py-3">
            <p className="text-sm font-medium text-foreground-800">촬영일 기준으로 자동 저장</p>
            <p className="mt-1 text-xs leading-5 text-foreground-500">
              예: 2026년 7월 23일에 찍은 사진은 ‘2026년 7월 23일’ 앨범에 저장됩니다.
              촬영일 정보가 없으면 업로드한 날짜를 사용합니다.
            </p>
          </div>
        ) : (
          <label className="mb-4 block text-xs font-medium text-foreground-600">
            사진을 저장할 앨범
            <select
              value={selectedAlbumId}
              onChange={(event) => setSelectedAlbumId(event.target.value)}
              disabled={addingToQueue}
              className="mt-2 w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-800"
            >
              <option value="">앨범을 선택해 주세요</option>
              {albums.map((album) => (
                <option key={album.id} value={album.id}>
                  {album.title} ({album.photoCount}장)
                </option>
              ))}
            </select>
          </label>
        )}

        <div
          className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 transition ${
            isDragging ? 'border-accent-500 bg-accent-50/50' : 'border-background-300'
          } ${addingToQueue ? 'pointer-events-none opacity-50' : ''}`}
          onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            void handleFileSelect(event.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,video/*,.heic,.heif,.hif,.mov,.mp4"
            multiple
            disabled={addingToQueue}
            onChange={(event) => {
              const input = event.currentTarget;
              void handleFileSelect(input.files);
              input.value = '';
            }}
          />
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent-500">
            <i className="ri-image-add-line text-2xl text-background-50" />
          </div>
          <p className="text-sm font-medium text-foreground-800">사진을 끌어다 놓거나 선택하세요</p>
          <p className="mt-2 text-[11px] text-foreground-400">사진 50MB·최대 20장 / MP4·MOV 500MB·최대 5개</p>
          <p className="mt-1 text-center text-[10px] leading-4 text-foreground-400">
            아이폰 Safari가 파일 형식을 비워 보내거나 JPEG로 변환해도 실제 파일 내용을 확인해 서버에 전송합니다.
            이미 등록된 같은 사진은 자동으로 제외합니다. 업로드 중에도 앱 안의 다른 메뉴를 사용할 수 있습니다.
          </p>
        </div>

        {error && (
          <div role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
            {error}
          </div>
        )}

        {queueMessage && (
          <div role="status" className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs leading-5 text-emerald-700">
            <p className="font-semibold">작업 대기열에 추가됐어요</p>
            <p className="mt-0.5">{queueMessage}</p>
            <p className="mt-1 text-[10px] text-emerald-600">사진은 2장씩, 동영상은 1개씩 처리합니다. 브라우저 새로고침이나 앱 종료는 완료 후 해주세요.</p>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <section className="mt-6">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-medium text-foreground-800">
                선택한 파일 <span className="text-accent-500">{selectedFiles.length}개</span>
              </p>
              <button type="button" onClick={clearAll} disabled={addingToQueue} className="text-xs text-foreground-500">
                모두 지우기
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {selectedFiles.map((item, index) => (
                <div key={`${item.file.name}_${item.file.lastModified}_${index}`} className="relative aspect-square overflow-hidden rounded-lg bg-secondary-100">
                  {isVideoFile(item.file) ? (
                    isMovFile(item.file) ? (
                      <div className="flex h-full w-full flex-col items-center justify-center bg-secondary-50 px-2 text-center">
                        <i className="ri-video-line text-2xl text-accent-500" />
                        <span className="mt-1 text-[10px] font-semibold text-foreground-500">MOV 동영상</span>
                        <span className="mt-0.5 text-[9px] text-foreground-400">서버에서 MP4로 변환</span>
                      </div>
                    ) : (
                      <>
                        <video src={item.previewUrl} className="h-full w-full object-cover" muted preload="metadata" />
                        <span className="absolute inset-0 flex items-center justify-center">
                          <i className="ri-play-circle-fill text-3xl text-white drop-shadow" />
                        </span>
                      </>
                    )
                  ) : isHeicFile(item.file) ? (
                    <div className="flex h-full w-full flex-col items-center justify-center bg-secondary-50 px-2 text-center">
                      <i className="ri-image-line text-2xl text-accent-500" />
                      <span className="mt-1 text-[10px] font-semibold text-foreground-500">HEIC 사진</span>
                    </div>
                  ) : (
                    <img src={item.previewUrl} alt={item.file.name} className="h-full w-full object-cover" />
                  )}
                  {!addingToQueue && (
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      aria-label={`${item.file.name} 제거`}
                      className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-foreground-950/70 text-white"
                    >
                      <i className="ri-close-line" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleUpload}
              disabled={addingToQueue}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-accent-500 py-3.5 text-sm font-medium text-background-50 disabled:opacity-50"
            >
              {addingToQueue
                ? '작업 대기열에 추가 중…'
                : organizeMode === 'date'
                  ? `${selectedFiles.length}개 날짜별 업로드 시작`
                  : `${selectedFiles.length}개 앨범 업로드 시작`}
            </button>
          </section>
        )}
      </main>

    </div>
  );
}
